import { z } from 'zod';
/**
 * Classified LLM-provider failure kinds. Used when a model call fails so
 * operators see actionable UX (credits exhausted vs bad key vs rate limit)
 * instead of a raw `AI_APICallError` blob.
 *
 * Grok/xAI note: out-of-credits often arrives as HTTP **402**, or as **403**
 * with body text like `personal-team-blocked:spending-limit` / "run out of
 * credits". Those must NOT be treated as auth (401) — refreshing the key
 * does not help.
 */
export const ProviderFailureKindSchema = z.enum([
    /** Prepaid credits / spend limit / billing inactive (e.g. xAI 402/403 spend). */
    'billing',
    /** Invalid / revoked / unauthorized API key (typically 401). */
    'auth',
    /** Provider throttling (429). Often retryable after a delay. */
    'rate_limit',
    /** Provider 5xx / overloaded / capacity. */
    'provider_unavailable',
    /** Malformed request / unsupported content (400). */
    'bad_request',
    /** Catch-all for unclassified provider failures. */
    'provider_error',
]);
export const ProviderFailureSchema = z.object({
    kind: ProviderFailureKindSchema,
    /** HTTP status from the provider when known (ai-sdk `APICallError.statusCode`). */
    statusCode: z.number().int().optional(),
    /** Whether a blind retry is reasonable (rate limits / transient 5xx). */
    retryable: z.boolean(),
    /** Original provider / SDK error message (truncated). */
    message: z.string(),
    /** Short operator-facing title for banners / cards. */
    title: z.string(),
    /** Concrete next step (top up credits, rotate key, wait, …). */
    remediation: z.string(),
    /**
     * Best-effort provider family inferred from endpoint / message / model id
     * (e.g. `xai` for Grok). Informational only — never required for UX.
     */
    providerHint: z.string().optional(),
});
const billingRe = /\b(out of credits?|insufficient[_ ]?(quota|funds|balance|credits?)|credit(?:s)? (?:exhausted|depleted|limit)|billing[_ ]?(not[_ ]active|hard[_ ]limit)|payment[_ ]required|spend(?:ing)?[_ -]?limit|prepaid|top[- ]?up|add credits?|personal-team-blocked|need a grok subscription|run out of (?:available )?credits?|quota[_ ]?exceeded)\b/i;
const authRe = /\b(invalid[_ ]?(api[_ ]?)?key|incorrect[_ ]api[_ ]key|unauthorized|authentication[_ ]?(failed|error)|api key (?:is )?invalid|invalid_api_key|permission[_ ]denied|not authenticated)\b/i;
const rateLimitRe = /\b(rate[_ ]?limit(?:ed)?|too many requests|tokens? per (?:min|minute)|rpm|tpm|retry[- ]after)\b/i;
const unavailableRe = /\b(overloaded|capacity|service[_ ]unavailable|bad gateway|gateway timeout|internal server error|temporarily unavailable)\b/i;
const xaiHintRe = /\b(xai|x\.ai|grok)\b/i;
/**
 * Human copy for a classified failure. Titles stay short for banners;
 * remediations tell the operator what to do next.
 */
export function describeProviderFailure(kind, opts) {
    const isXai = opts?.providerHint === 'xai';
    switch (kind) {
        case 'billing':
            return {
                title: isXai ? 'Grok / xAI credits exhausted' : 'Provider credits exhausted',
                remediation: isXai
                    ? 'Add credits or raise the spend limit at console.x.ai, then retry. Updating the API key alone will not fix this.'
                    : 'Top up the provider account or raise the spend/quota limit, then retry. Rotating the API key alone usually will not help.',
                retryable: false,
            };
        case 'auth':
            return {
                title: 'API key rejected',
                remediation: 'Open Model Configs, paste a valid key for this provider, and re-test. Revoked or mistyped keys return this error.',
                retryable: false,
            };
        case 'rate_limit':
            return {
                title: 'Provider rate limit',
                remediation: 'The provider throttled this account. Wait a moment and retry, or lower concurrency / switch models.',
                retryable: true,
            };
        case 'provider_unavailable':
            return {
                title: 'Provider temporarily unavailable',
                remediation: 'The model provider returned a server error. Retry shortly; if it persists, check their status page.',
                retryable: true,
            };
        case 'bad_request':
            return {
                title: 'Provider rejected the request',
                remediation: 'The call was malformed or used unsupported content for this model. Check prompts, tools, and multimodal payloads.',
                retryable: false,
            };
        case 'provider_error':
            return {
                title: opts?.statusCode ? `Provider error (${opts.statusCode})` : 'Provider error',
                remediation: 'Inspect the details below. If this keeps happening, verify the model config and provider status.',
                retryable: false,
            };
        default: {
            const _exhaustive = kind;
            return _exhaustive;
        }
    }
}
function inferProviderHint(text, context) {
    const hay = `${text}\n${context ?? ''}`;
    if (xaiHintRe.test(hay))
        return 'xai';
    return undefined;
}
/**
 * Classify a provider failure from HTTP status + message text.
 * Prefer an explicit `statusCode` when available; fall back to message heuristics
 * (needed for persisted run/PA errors that only kept `Error.message`).
 */
export function classifyProviderFailureFromText(input) {
    const message = (input.text ?? '').trim() || 'Unknown provider error';
    const statusCode = typeof input.statusCode === 'number' && Number.isFinite(input.statusCode)
        ? input.statusCode
        : extractStatusCodeFromText(message);
    const providerHint = inferProviderHint(message, input.context);
    const kind = classifyKind(message, statusCode);
    const described = describeProviderFailure(kind, { providerHint, statusCode });
    return {
        kind,
        statusCode: statusCode ?? undefined,
        retryable: described.retryable,
        message: message.slice(0, 2000),
        title: described.title,
        remediation: described.remediation,
        providerHint,
    };
}
function classifyKind(message, statusCode) {
    // Status-first for unambiguous codes; refine 403 with billing body text
    // (xAI spending-limit returns 403, not 401).
    if (statusCode === 402 || (statusCode === 403 && billingRe.test(message))) {
        return 'billing';
    }
    if (statusCode === 401) {
        // Some providers stuff "insufficient_quota" under 401 — prefer billing.
        if (billingRe.test(message))
            return 'billing';
        return 'auth';
    }
    if (statusCode === 429)
        return 'rate_limit';
    if (statusCode === 400) {
        if (billingRe.test(message))
            return 'billing';
        return 'bad_request';
    }
    if (statusCode !== undefined && statusCode >= 500)
        return 'provider_unavailable';
    if (billingRe.test(message))
        return 'billing';
    if (authRe.test(message))
        return 'auth';
    if (rateLimitRe.test(message))
        return 'rate_limit';
    if (unavailableRe.test(message))
        return 'provider_unavailable';
    if (statusCode === 403)
        return 'auth';
    return 'provider_error';
}
/** Pull `statusCode: 402` / `Error code: 403` / `HTTP 402` / `(402)` out of a message blob. */
function extractStatusCodeFromText(text) {
    const patterns = [
        /\bstatus(?:Code)?["\s:=]+(\d{3})\b/i,
        /\bError code:\s*(\d{3})\b/i,
        /\bHTTP\s+(\d{3})\b/i,
        /\((\d{3})\)/,
    ];
    for (const re of patterns) {
        const m = re.exec(text);
        if (!m)
            continue;
        const n = Number(m[1]);
        if (n >= 400 && n < 600)
            return n;
    }
    return undefined;
}
/**
 * Best-effort extraction of a ProviderFailure from a persisted run/PA error
 * payload (JSON blob or string). Prefers an embedded `failure` / `kind` field
 * when present; otherwise classifies from `message`.
 */
export function providerFailureFromUnknown(error) {
    if (error == null)
        return null;
    if (typeof error === 'string') {
        return classifyProviderFailureFromText({ text: error });
    }
    if (typeof error !== 'object') {
        return classifyProviderFailureFromText({ text: String(error) });
    }
    const obj = error;
    // Nested shapes: { details: { failure } } or { failure }
    const nestedFailure = obj.failure ?? obj.details?.failure;
    if (nestedFailure && typeof nestedFailure === 'object') {
        const parsed = ProviderFailureSchema.safeParse(nestedFailure);
        if (parsed.success)
            return parsed.data;
    }
    const kindRaw = obj.kind ?? obj.details?.kind;
    const message = (typeof obj.message === 'string' && obj.message) ||
        (typeof obj.details?.message === 'string'
            ? obj.details.message
            : null) ||
        safeJson(obj);
    const statusCode = typeof obj.statusCode === 'number'
        ? obj.statusCode
        : typeof obj.details?.statusCode === 'number'
            ? obj.details.statusCode
            : undefined;
    if (typeof kindRaw === 'string' && ProviderFailureKindSchema.safeParse(kindRaw).success) {
        const kind = kindRaw;
        const described = describeProviderFailure(kind, { statusCode });
        return {
            kind,
            statusCode,
            retryable: typeof obj.retryable === 'boolean' ? obj.retryable : described.retryable,
            message: (message ?? described.title).slice(0, 2000),
            title: described.title,
            remediation: described.remediation,
        };
    }
    if (!message)
        return null;
    return classifyProviderFailureFromText({ text: message, statusCode });
}
function safeJson(value) {
    try {
        return JSON.stringify(value);
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=provider-failure.js.map